import { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import {
  Upload,
  FileText,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function MedicalBulkUploadPage() {
  const { token } = useAuth();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [results, setResults] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [jobProgress, setJobProgress] = useState(null);
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  const handleCleanupBulkFiles = async () => {
    if (!window.confirm("This will remove incorrectly stored full-PDF copies from previous uploads and reset affected person records. You can then re-upload the scans. Continue?")) return;
    setCleaning(true);
    try {
      const res = await axios.post(`${API}/medical/cleanup-bulk-files`, {}, { headers });
      toast.success(res.data.message);
    } catch {
      toast.error("Cleanup failed");
    } finally {
      setCleaning(false);
    }
  };

  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files || []);
    setFiles(selected);
    setResults(null);
    setJobId(null);
    setJobProgress(null);
  };

  const pollJob = useCallback(async (jid) => {
    try {
      const res = await axios.get(`${API}/medical/upload/job/${jid}`, { headers });
      const job = res.data;
      setJobProgress(job);

      if (job.status === "complete" || job.status === "error") {
        // Done polling
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = null;
        setUploading(false);
        setJobId(null);

        if (job.status === "complete" && job.results) {
          const uploaded = job.total_matched || 0;
          const unmatched = job.total_unmatched || 0;
          const errors = job.total_errors || 0;
          setResults({
            uploaded,
            unmatched,
            errors,
            results: job.results,
          });
          toast.success(`Upload complete: ${uploaded} matched, ${unmatched} unmatched`);
        } else if (job.status === "error") {
          toast.error(`Upload failed: ${job.error || "Unknown error"}`);
        }
      }
    } catch {
      // Ignore transient poll errors
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const handleUpload = async () => {
    if (files.length === 0) {
      toast.error("Select files first");
      return;
    }
    setUploading(true);
    setResults(null);
    setJobProgress(null);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));

      const res = await axios.post(`${API}/medical/upload/bulk`, fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });

      if (res.data.job_id) {
        setJobId(res.data.job_id);
        setJobProgress({ status: "processing", ocr_progress: "Starting OCR...", processed_files: 0, total_files: res.data.total_files });
        // Start polling every 2 seconds
        pollRef.current = setInterval(() => pollJob(res.data.job_id), 2000);
      } else {
        // Direct result (shouldn't happen with new backend, but fallback)
        setResults(res.data);
        setUploading(false);
        toast.success(`Upload complete: ${res.data.uploaded} matched, ${res.data.unmatched} unmatched`);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.response?.data?.error || err.message || "Upload failed";
      toast.error(`Upload failed: ${detail}`);
      setUploading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "uploaded":
        return <CheckCircle className="h-5 w-5 text-emerald-500" />;
      case "unmatched":
        return <AlertTriangle className="h-5 w-5 text-amber-500" />;
      case "processing":
        return <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />;
      default:
        return <XCircle className="h-5 w-5 text-red-500" />;
    }
  };

  return (
    <div className="space-y-6" data-testid="medical-bulk-upload">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
          Medical Forms — Bulk Upload
        </h1>
        <p className="text-slate-500 mt-1">
          Upload Physical Part 2 forms. OCR will auto-match to persons.
        </p>
      </div>

      {/* Upload Area */}
      <div className="bg-white border border-slate-200 rounded-lg p-8">
        <div className="flex items-center justify-between mb-4">
          <div />
          <Button
            variant="outline"
            size="sm"
            onClick={handleCleanupBulkFiles}
            disabled={cleaning || uploading}
            className="text-xs text-red-600 border-red-200 hover:bg-red-50"
            data-testid="medical-cleanup-bulk-btn"
          >
            <Trash2 className="h-3.5 w-3.5 mr-1.5" />
            {cleaning ? "Cleaning..." : "Cleanup Old Uploads"}
          </Button>
        </div>
        <div
          className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-teal-400 transition-colors cursor-pointer"
          onClick={() => !uploading && fileInputRef.current?.click()}
          data-testid="medical-upload-dropzone"
        >
          <Upload className="h-10 w-10 text-slate-400 mx-auto mb-3" />
          <p className="text-sm text-slate-600 font-medium">
            Click to select files or drag & drop
          </p>
          <p className="text-xs text-slate-400 mt-1">
            PDF, JPG, PNG — up to 25MB each
          </p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.jpg,.jpeg,.png"
            className="hidden"
            onChange={handleFileSelect}
            data-testid="medical-upload-input"
          />
        </div>

        {files.length > 0 && (
          <div className="mt-4">
            <p className="text-sm text-slate-600 font-medium mb-2">
              {files.length} file{files.length !== 1 ? "s" : ""} selected
            </p>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {files.map((f, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 text-xs text-slate-500"
                >
                  <FileText className="h-3 w-3" />
                  {f.name}{" "}
                  <span className="text-slate-300">
                    ({(f.size / 1024).toFixed(0)} KB)
                  </span>
                </div>
              ))}
            </div>
            <Button
              onClick={handleUpload}
              disabled={uploading}
              className="mt-4 bg-teal-700 hover:bg-teal-800"
              data-testid="medical-upload-submit"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload & Process
                </>
              )}
            </Button>
          </div>
        )}
      </div>

      {/* Progress Indicator */}
      {jobProgress && jobProgress.status === "processing" && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6" data-testid="medical-upload-progress">
          <div className="flex items-center gap-3 mb-3">
            <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
            <h2 className="font-heading font-semibold text-blue-900">
              Processing Upload...
            </h2>
          </div>
          <p className="text-sm text-blue-700">
            {jobProgress.ocr_progress || "Processing..."}
          </p>
          {jobProgress.total_files > 0 && (
            <div className="mt-3">
              <div className="w-full bg-blue-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${Math.max(5, (jobProgress.processed_files / jobProgress.total_files) * 100)}%` }}
                />
              </div>
              <p className="text-xs text-blue-500 mt-1">
                File {jobProgress.processed_files + 1} of {jobProgress.total_files}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Results */}
      {results && (
        <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4" data-testid="medical-upload-results">
          <h2 className="font-heading font-semibold text-lg text-slate-900">
            Upload Results
          </h2>

          <div className="flex gap-4 text-sm">
            <span className="text-emerald-600 font-semibold">
              {results.uploaded} matched
            </span>
            <span className="text-amber-600 font-semibold">
              {results.unmatched} unmatched
            </span>
            {results.errors > 0 && (
              <span className="text-red-600 font-semibold">
                {results.errors} errors
              </span>
            )}
          </div>

          <div className="divide-y divide-slate-100">
            {results.results?.map((r, i) => (
              <div key={i} className="py-3 flex items-start gap-3">
                {getStatusIcon(r.status)}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-700 truncate">
                    {r.original_filename}
                  </p>
                  {r.person_matches?.length > 0 && (
                    <div className="mt-1 space-y-0.5">
                      {r.person_matches.map((m, j) => (
                        <p key={j} className="text-xs text-slate-500">
                          Matched: <strong>{m.person_name}</strong>
                          {m.pages && ` (page${m.pages.length > 1 ? 's' : ''} ${m.pages.map(p => p + 1).join(', ')})`}
                          {m.match_score && ` · score: ${m.match_score}`}
                          {m.duplicate && " (duplicate)"}
                          {m.expiration_date &&
                            ` · Exp: ${new Date(m.expiration_date).toLocaleDateString()}`}
                          {m.ocr_snippet && (
                            <span className="block text-[10px] text-slate-400 mt-0.5 italic truncate max-w-lg">
                              OCR: {m.ocr_snippet.slice(0, 100)}...
                            </span>
                          )}
                        </p>
                      ))}
                    </div>
                  )}
                  {r.error && (
                    <p className="text-xs text-amber-600 mt-1">{r.error}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
